import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Megaphone, Pencil, Plus, Trash2 } from "lucide-react";

import { adminSettingsApi } from "@profitlens/api-client";
import type { AnnouncementLevel, AnnouncementOut } from "@profitlens/api-client";
import { Badge } from "@profitlens/ui/badge";
import { Button } from "@profitlens/ui/button";
import { Card, CardContent, CardHeader } from "@profitlens/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@profitlens/ui/dialog";
import { ErrorState } from "@profitlens/ui/error-state";
import { Input } from "@profitlens/ui/input";
import { Label } from "@profitlens/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@profitlens/ui/select";
import { Spinner } from "@profitlens/ui/spinner";
import { Switch } from "@profitlens/ui/switch";

import { PageHeader } from "@/components/page-header";
import { translateError } from "@/lib/api-error";
import { formatDateTime } from "@/lib/format";

const LEVEL_KEYS: Record<AnnouncementLevel, string> = {
  info: "ops.levelInfo",
  warning: "ops.levelWarning",
  critical: "ops.levelCritical",
};

function LevelBadge({ level }: { level: AnnouncementLevel }) {
  const { t } = useTranslation();
  if (level === "critical") return <Badge variant="danger">{t(LEVEL_KEYS[level])}</Badge>;
  if (level === "warning") return <Badge variant="warning">{t(LEVEL_KEYS[level])}</Badge>;
  return <Badge variant="secondary">{t(LEVEL_KEYS[level])}</Badge>;
}

export function AnnouncementsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<AnnouncementOut | "new" | null>(null);

  const list = useQuery({
    queryKey: ["admin", "announcements"],
    queryFn: () => adminSettingsApi.listAnnouncements(),
  });

  const remove = useMutation({
    mutationFn: (id: string) => adminSettingsApi.deleteAnnouncement(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "announcements"] });
    },
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("ops.announcementsTitle")}
        subtitle={t("ops.announcementsSubtitle")}
        actions={
          <Button variant="outline" onClick={() => setEditing("new")}>
            <Plus className="h-4 w-4" />
            {t("ops.announcementAdd")}
          </Button>
        }
      />

      {list.isLoading ? (
        <div className="grid place-items-center py-12">
          <Spinner className="h-5 w-5" />
        </div>
      ) : list.error ? (
        <ErrorState error={list.error} onRetry={() => list.refetch()} />
      ) : list.data && list.data.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted">{t("ops.announcementsEmpty")}</Card>
      ) : (
        <div className="space-y-3">
          {list.data?.map((item) => (
            <Card key={item.id}>
              <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 p-4 pb-2">
                <div className="flex min-w-0 items-center gap-2">
                  <Megaphone className="h-4 w-4 shrink-0 text-brand" />
                  <p className="truncate text-sm font-semibold">{item.title}</p>
                  <LevelBadge level={item.level} />
                  {!item.enabled ? <Badge variant="secondary">{t("common.disabled")}</Badge> : null}
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => setEditing(item)}>
                    <Pencil className="h-3.5 w-3.5" />
                    {t("common.edit")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (window.confirm(t("common.confirmDelete"))) remove.mutate(item.id);
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-danger" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                {item.body ? (
                  <p className="line-clamp-3 text-sm whitespace-pre-wrap text-muted">{item.body}</p>
                ) : null}
                <p className="mt-2 text-xs text-muted">
                  {t("ops.announcementWindow")}：{" "}
                  {item.starts_at ? formatDateTime(item.starts_at) : t("common.never")}
                  {" → "}
                  {item.ends_at ? formatDateTime(item.ends_at) : t("common.never")}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AnnouncementDialog editing={editing} onClose={() => setEditing(null)} />
    </div>
  );
}

/** datetime-local <-> ISO 互转（本地时区）。 */
function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const textareaClass =
  "min-h-28 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder:text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50";

function AnnouncementDialog({
  editing,
  onClose,
}: {
  editing: AnnouncementOut | "new" | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isNew = editing === "new";
  const existing = editing !== null && editing !== "new" ? editing : null;

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [level, setLevel] = useState<AnnouncementLevel>("info");
  const [enabled, setEnabled] = useState(true);
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (existing) {
      setTitle(existing.title);
      setBody(existing.body);
      setLevel(existing.level);
      setEnabled(existing.enabled);
      setStartsAt(toLocalInput(existing.starts_at));
      setEndsAt(toLocalInput(existing.ends_at));
    } else {
      setTitle("");
      setBody("");
      setLevel("info");
      setEnabled(true);
      setStartsAt("");
      setEndsAt("");
    }
    setError(null);
  }, [editing, existing]);

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        title,
        body,
        level,
        enabled,
        starts_at: toIso(startsAt),
        ends_at: toIso(endsAt),
      };
      if (isNew) return adminSettingsApi.createAnnouncement(payload);
      if (!existing) throw new Error("no announcement");
      return adminSettingsApi.patchAnnouncement(existing.id, payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "announcements"] });
      onClose();
    },
    onError: (err) => setError(translateError(err, t)),
  });

  return (
    <Dialog open={editing !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isNew ? t("ops.announcementAdd") : t("ops.announcementEdit")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t("common.title")}</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>{t("common.content")}</Label>
            <textarea
              className={textareaClass}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("ops.announcementLevel")}</Label>
              <Select value={level} onValueChange={(v) => setLevel(v as AnnouncementLevel)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">{t("ops.levelInfo")}</SelectItem>
                  <SelectItem value="warning">{t("ops.levelWarning")}</SelectItem>
                  <SelectItem value="critical">{t("ops.levelCritical")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={enabled} onCheckedChange={setEnabled} />
                {t("ops.announcementEnabled")}
              </label>
            </div>
            <div className="space-y-1.5">
              <Label>{t("ops.announcementStartsAt")}</Label>
              <Input
                type="datetime-local"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("ops.announcementEndsAt")}</Label>
              <Input
                type="datetime-local"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
              />
            </div>
          </div>
          <p className="text-xs text-muted">{t("ops.announcementWindowHint")}</p>
          {error ? <p className="text-xs text-danger">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button disabled={save.isPending || !title} onClick={() => save.mutate()}>
            {save.isPending ? t("common.saving") : t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
